import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../utils/axiosInstance';
import {
  loadCourses,
  createCourse,
  updateCourse,
  deleteCourse,
  countLessons,
  getCourseBatches,
  addCourseBatch,
  removeCourseBatch,
  getCourseStaff,
  addCourseStaff,
  removeCourseStaff,
} from '../../utils/studyMaterialsApi';

const STATUS_STYLES = {
  draft:     { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Draft' },
  published: { bg: 'bg-green-100', text: 'text-green-700', label: 'Published' },
};

function fmtDate(s) {
  if (!s) return '—';
  return new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).format(new Date(s));
}

function FileIcon() {
  return (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5.586a1 1 0 0 1 .707.293l5.414 5.414a1 1 0 0 1 .293.707V19a2 2 0 0 1-2 2z" />
    </svg>
  );
}

function NewCourseModal({ onClose, onCreated }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [creating, setCreating] = useState(false);

  const submit = async () => {
    if (!title.trim()) return;
    setCreating(true);
    try {
      const course = await createCourse({ title, description });
      onCreated(course);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-base font-bold text-slate-800">New Course</h3>
        <div className="mt-4 space-y-3">
          <div>
            <label className="mb-1 block text-xs font-semibold text-slate-500">Title *</label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              autoFocus
              placeholder="e.g. The Complete FastAPI Developer Course"
              className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-800 outline-none focus:border-violet-400 focus:ring-2 focus:ring-violet-100"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold text-slate-500">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              placeholder="Brief description of this course…"
              className="w-full resize-none rounded-xl border border-slate-200 px-4 py-2.5 text-sm text-slate-700 outline-none focus:border-violet-400 focus:ring-2 focus:ring-violet-100"
            />
          </div>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600 hover:bg-slate-50 transition"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={creating || !title.trim()}
            className="rounded-lg bg-violet-600 px-4 py-2 text-sm font-semibold text-white hover:bg-violet-700 disabled:opacity-50 transition"
          >
            {creating ? 'Creating…' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
}

function EditCourseModal({ course, onClose, onSaved }) {
  const [title, setTitle] = useState(course.title);
  const [description, setDescription] = useState(course.description || '');
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!title.trim()) return;
    setSaving(true);
    try {
      const updated = await updateCourse(course.id, { title: title.trim(), description: description.trim() });
      onSaved(updated);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-base font-bold text-slate-800">Edit Course</h3>
        <div className="mt-4 space-y-3">
          <div>
            <label className="mb-1 block text-xs font-semibold text-slate-500">Title *</label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              autoFocus
              className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-800 outline-none focus:border-violet-400 focus:ring-2 focus:ring-violet-100"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold text-slate-500">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="w-full resize-none rounded-xl border border-slate-200 px-4 py-2.5 text-sm text-slate-700 outline-none focus:border-violet-400 focus:ring-2 focus:ring-violet-100"
            />
          </div>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600 hover:bg-slate-50 transition"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={saving || !title.trim()}
            className="rounded-lg bg-violet-600 px-4 py-2 text-sm font-semibold text-white hover:bg-violet-700 disabled:opacity-50 transition"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ShareBatchesModal({ course, onClose, onChanged }) {
  const [shared, setShared] = useState([]);
  const [loading, setLoading] = useState(true);
  const [allBatches, setAllBatches] = useState([]);
  const [search, setSearch] = useState('');
  const [busyBatchId, setBusyBatchId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      getCourseBatches(course.id),
      axiosInstance.get('/admin/batches').then((r) => (Array.isArray(r.data) ? r.data : r.data?.batches || [])),
    ]).then(([sharedRows, batches]) => {
      if (cancelled) return;
      setShared(sharedRows);
      setAllBatches(batches);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [course.id]);

  const sharedIds = useMemo(() => new Set(shared.map((s) => s.batchId)), [shared]);

  const results = useMemo(() => {
    if (!search.trim()) return [];
    const q = search.trim().toLowerCase();
    return allBatches
      .filter((b) => !sharedIds.has(b.batchId))
      .filter((b) =>
        b.batchName?.toLowerCase().includes(q) ||
        b.batchInternalName?.toLowerCase().includes(q) ||
        b.whatsappGroupName?.toLowerCase().includes(q) ||
        b.course?.shortName?.toLowerCase().includes(q)
      )
      .slice(0, 20);
  }, [allBatches, sharedIds, search]);

  const handleAdd = async (batch) => {
    setBusyBatchId(batch.batchId);
    try {
      await addCourseBatch(course.id, batch.batchId);
      setShared((prev) => [
        {
          id: `local-${batch.batchId}`,
          batchId: batch.batchId,
          batchName: batch.batchName,
          batchInternalName: batch.batchInternalName,
          whatsappGroupName: batch.whatsappGroupName,
          courseShortName: batch.course?.shortName,
          sharedAt: new Date().toISOString(),
        },
        ...prev,
      ]);
      setSearch('');
      onChanged?.();
    } finally {
      setBusyBatchId(null);
    }
  };

  const handleRemove = async (batchId) => {
    setBusyBatchId(batchId);
    try {
      await removeCourseBatch(course.id, batchId);
      setShared((prev) => prev.filter((s) => s.batchId !== batchId));
      onChanged?.();
    } finally {
      setBusyBatchId(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="flex max-h-[80vh] w-full max-w-md flex-col rounded-2xl bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="border-b border-slate-100 px-6 py-4">
          <h3 className="text-base font-bold text-slate-800">Share with Batches</h3>
          <p className="mt-0.5 line-clamp-1 text-xs text-slate-500">{course.title}</p>
        </div>

        <div className="border-b border-slate-100 px-6 py-3">
          <div className="relative">
            <svg className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
            </svg>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search batch to add…"
              className="w-full rounded-lg border border-slate-200 py-2 pl-9 pr-3 text-sm outline-none focus:border-violet-400 focus:ring-2 focus:ring-violet-100"
            />
          </div>
          {search.trim() && (
            <div className="mt-2 max-h-40 overflow-y-auto rounded-lg border border-slate-100">
              {results.length === 0 ? (
                <p className="px-3 py-3 text-center text-xs text-slate-400">No available batches match.</p>
              ) : (
                results.map((b) => (
                  <button
                    key={b.batchId}
                    onClick={() => handleAdd(b)}
                    disabled={busyBatchId === b.batchId}
                    className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-xs hover:bg-violet-50 transition disabled:opacity-50"
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-medium text-slate-700">
                        {b.batchName || b.batchInternalName || b.whatsappGroupName}
                      </span>
                      <span className="flex items-center gap-1.5 text-[10px] text-slate-400">
                        {b.batchInternalName && b.batchInternalName !== b.batchName && (
                          <span className="truncate">{b.batchInternalName}</span>
                        )}
                        {b.course?.shortName && <span className="shrink-0">{b.course.shortName}</span>}
                      </span>
                    </span>
                    <span className="shrink-0 font-semibold text-violet-600">+ Add</span>
                  </button>
                ))
              )}
            </div>
          )}
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-3">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-xs font-bold uppercase tracking-wide text-slate-400">Shared batches</span>
            <span className="text-xs text-slate-400">{shared.length}</span>
          </div>
          {loading ? (
            <p className="py-6 text-center text-xs text-slate-400">Loading…</p>
          ) : shared.length === 0 ? (
            <p className="py-6 text-center text-xs text-slate-400">Not shared with any batch yet.</p>
          ) : (
            <div className="space-y-1.5">
              {shared.map((b) => (
                <div key={b.batchId} className="flex items-center gap-2 rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
                  <svg className="h-3.5 w-3.5 shrink-0 text-emerald-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16zm3.707-9.293a1 1 0 0 0-1.414-1.414L9 10.586 7.707 9.293a1 1 0 0 0-1.414 1.414l2 2a1 1 0 0 0 1.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-xs font-medium text-slate-700">
                      {b.batchName || b.batchInternalName || b.whatsappGroupName}
                    </span>
                    {b.batchInternalName && b.batchInternalName !== b.batchName && (
                      <span className="block truncate text-[10px] text-slate-400">{b.batchInternalName}</span>
                    )}
                  </span>
                  {b.courseShortName && (
                    <span className="shrink-0 rounded-full bg-slate-200 px-1.5 py-0.5 text-[9px] text-slate-500">{b.courseShortName}</span>
                  )}
                  <button
                    onClick={() => handleRemove(b.batchId)}
                    disabled={busyBatchId === b.batchId}
                    className="shrink-0 text-slate-300 hover:text-red-500 disabled:opacity-50 transition"
                    title="Remove access"
                  >
                    <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="border-t border-slate-100 px-6 py-3">
          <button
            onClick={onClose}
            className="w-full rounded-lg border border-slate-200 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

function ShareStaffModal({ course, onClose, onChanged }) {
  const [shared, setShared] = useState([]);
  const [loading, setLoading] = useState(true);
  const [allStaff, setAllStaff] = useState([]);
  const [search, setSearch] = useState('');
  const [busyStaffId, setBusyStaffId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      getCourseStaff(course.id),
      axiosInstance.get('/admin/staff').then((r) => r.data?.staff || []),
    ]).then(([sharedRows, staff]) => {
      if (cancelled) return;
      setShared(sharedRows);
      setAllStaff(staff.filter((s) => s.loginStatus === 'active'));
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [course.id]);

  const sharedIds = useMemo(() => new Set(shared.map((s) => s.staffId)), [shared]);

  const results = useMemo(() => {
    if (!search.trim()) return [];
    const q = search.trim().toLowerCase();
    return allStaff
      .filter((s) => !sharedIds.has(s.staffId))
      .filter((s) =>
        s.name?.toLowerCase().includes(q) ||
        s.officeEmail?.toLowerCase().includes(q) ||
        s.position?.toLowerCase().includes(q)
      )
      .slice(0, 20);
  }, [allStaff, sharedIds, search]);

  const handleAdd = async (staff) => {
    setBusyStaffId(staff.staffId);
    try {
      await addCourseStaff(course.id, staff.staffId);
      setShared((prev) => [
        {
          id: `local-${staff.staffId}`,
          staffId: staff.staffId,
          name: staff.name,
          officeEmail: staff.officeEmail,
          position: staff.position,
          sharedAt: new Date().toISOString(),
        },
        ...prev,
      ]);
      setSearch('');
      onChanged?.();
    } finally {
      setBusyStaffId(null);
    }
  };

  const handleRemove = async (staffId) => {
    setBusyStaffId(staffId);
    try {
      await removeCourseStaff(course.id, staffId);
      setShared((prev) => prev.filter((s) => s.staffId !== staffId));
      onChanged?.();
    } finally {
      setBusyStaffId(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="flex max-h-[80vh] w-full max-w-md flex-col rounded-2xl bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="border-b border-slate-100 px-6 py-4">
          <h3 className="text-base font-bold text-slate-800">Share with Staffs</h3>
          <p className="mt-0.5 line-clamp-1 text-xs text-slate-500">{course.title}</p>
        </div>

        <div className="border-b border-slate-100 px-6 py-3">
          <div className="relative">
            <svg className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
            </svg>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search active staff to add…"
              className="w-full rounded-lg border border-slate-200 py-2 pl-9 pr-3 text-sm outline-none focus:border-violet-400 focus:ring-2 focus:ring-violet-100"
            />
          </div>
          {search.trim() && (
            <div className="mt-2 max-h-40 overflow-y-auto rounded-lg border border-slate-100">
              {results.length === 0 ? (
                <p className="px-3 py-3 text-center text-xs text-slate-400">No available active staff match.</p>
              ) : (
                results.map((s) => (
                  <button
                    key={s.staffId}
                    onClick={() => handleAdd(s)}
                    disabled={busyStaffId === s.staffId}
                    className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-xs hover:bg-violet-50 transition disabled:opacity-50"
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-medium text-slate-700">{s.name}</span>
                      <span className="flex items-center gap-1.5 text-[10px] text-slate-400">
                        {s.position && <span className="truncate">{s.position}</span>}
                        {s.officeEmail && <span className="truncate">{s.officeEmail}</span>}
                      </span>
                    </span>
                    <span className="shrink-0 font-semibold text-violet-600">+ Add</span>
                  </button>
                ))
              )}
            </div>
          )}
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-3">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-xs font-bold uppercase tracking-wide text-slate-400">Shared with</span>
            <span className="text-xs text-slate-400">{shared.length}</span>
          </div>
          {loading ? (
            <p className="py-6 text-center text-xs text-slate-400">Loading…</p>
          ) : shared.length === 0 ? (
            <p className="py-6 text-center text-xs text-slate-400">Not shared with any staff yet.</p>
          ) : (
            <div className="space-y-1.5">
              {shared.map((s) => (
                <div key={s.staffId} className="flex items-center gap-2 rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
                  <svg className="h-3.5 w-3.5 shrink-0 text-emerald-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16zm3.707-9.293a1 1 0 0 0-1.414-1.414L9 10.586 7.707 9.293a1 1 0 0 0-1.414 1.414l2 2a1 1 0 0 0 1.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-xs font-medium text-slate-700">{s.name}</span>
                    {s.officeEmail && (
                      <span className="block truncate text-[10px] text-slate-400">{s.officeEmail}</span>
                    )}
                  </span>
                  {s.position && (
                    <span className="shrink-0 rounded-full bg-slate-200 px-1.5 py-0.5 text-[9px] text-slate-500">{s.position}</span>
                  )}
                  <button
                    onClick={() => handleRemove(s.staffId)}
                    disabled={busyStaffId === s.staffId}
                    className="shrink-0 text-slate-300 hover:text-red-500 disabled:opacity-50 transition"
                    title="Remove access"
                  >
                    <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="border-t border-slate-100 px-6 py-3">
          <button
            onClick={onClose}
            className="w-full rounded-lg border border-slate-200 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

export default function StudyMaterials() {
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statFilter, setStatFilter] = useState('all');
  const [showNewModal, setShowNewModal] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  const [sharingCourse, setSharingCourse] = useState(null);
  const [sharingStaffCourse, setSharingStaffCourse] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    loadCourses()
      .then((data) => { if (!cancelled) setCourses(data); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const filtered = useMemo(() => {
    let list = courses;
    if (statFilter !== 'all') list = list.filter((c) => c.status === statFilter);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter((c) => c.title?.toLowerCase().includes(q) || c.description?.toLowerCase().includes(q));
    }
    return [...list].sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
  }, [courses, search, statFilter]);

  const togglePublish = async (course) => {
    const nextStatus = course.status === 'published' ? 'draft' : 'published';
    const updated = await updateCourse(course.id, { status: nextStatus });
    setCourses((prev) => prev.map((c) => (c.id === updated.id ? { ...c, ...updated } : c)));
  };

  const handleDelete = async (course) => {
    if (!window.confirm(`Delete "${course.title}"? This will remove its entire content tree.`)) return;
    await deleteCourse(course.id);
    setCourses((prev) => prev.filter((c) => c.id !== course.id));
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="border-b border-slate-200 bg-white px-6 py-4 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate('/admin/dashboard')}
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-600 hover:bg-slate-50 transition"
            >
              <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
              Back
            </button>
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-violet-100">
              <svg className="h-5 w-5 text-violet-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
            </div>
            <div>
              <h1 className="text-lg font-bold text-slate-800">Study Materials</h1>
              <p className="text-xs text-slate-500">Create and manage course content for batches</p>
            </div>
          </div>
          <button
            onClick={() => setShowNewModal(true)}
            className="flex items-center gap-2 rounded-xl bg-violet-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-violet-700 active:scale-95 transition"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
            </svg>
            New Course
          </button>
        </div>

        {/* Filters */}
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-slate-50 px-3 py-1.5">
            <svg className="h-4 w-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
            </svg>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search courses…"
              className="w-44 bg-transparent text-sm text-slate-700 placeholder-slate-400 outline-none"
            />
          </div>
          <select
            value={statFilter}
            onChange={(e) => setStatFilter(e.target.value)}
            className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-700 outline-none focus:border-violet-400"
          >
            <option value="all">All Status</option>
            <option value="draft">Draft</option>
            <option value="published">Published</option>
          </select>
          {(search || statFilter !== 'all') && (
            <button
              onClick={() => { setSearch(''); setStatFilter('all'); }}
              className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs text-slate-500 hover:border-slate-400 hover:text-slate-700 transition"
            >
              Clear filters
            </button>
          )}
          <span className="ml-auto text-xs text-slate-400">{filtered.length} course{filtered.length !== 1 ? 's' : ''}</span>
        </div>
      </div>

      {/* Body */}
      <div className="px-6 py-6">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-24 text-slate-400">
            <svg className="h-8 w-8 animate-spin text-violet-500" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
            </svg>
            <p className="mt-3 text-sm">Loading courses…</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 text-slate-400">
            <FileIcon />
            <p className="mt-3 text-sm">{search || statFilter !== 'all' ? 'No results match your filters.' : 'No courses yet.'}</p>
            {!search && statFilter === 'all' && (
              <button
                onClick={() => setShowNewModal(true)}
                className="mt-4 rounded-xl bg-violet-600 px-5 py-2 text-sm font-semibold text-white hover:bg-violet-700 transition"
              >
                Create first course
              </button>
            )}
          </div>
        ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {filtered.map((course) => {
            const st = STATUS_STYLES[course.status] || STATUS_STYLES.draft;
            const lessonCount = course.lessonCount ?? countLessons(course.tree);
            return (
              <div
                key={course.id}
                className="group flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition hover:-translate-y-0.5 hover:border-violet-200 hover:shadow-lg"
              >
                <div className={`h-1 w-full shrink-0 ${course.status === 'published' ? 'bg-emerald-400' : 'bg-amber-400'}`} />

                <div className="flex flex-1 flex-col p-4">
                  <div className="mb-2 flex items-start justify-between gap-2">
                    <h3
                      onClick={() => navigate(`/admin/study-materials/edit/${course.id}`)}
                      title="Open course editor"
                      className="line-clamp-2 min-w-0 flex-1 cursor-pointer text-sm font-bold text-slate-800 leading-snug transition hover:text-violet-700"
                    >
                      {course.title}
                    </h3>
                    <div className="flex shrink-0 items-center gap-1.5">
                      <button
                        onClick={() => window.open(`/admin/study-materials/view/${course.id}`, '_blank', 'noopener,noreferrer')}
                        title="View as blog (new tab)"
                        className="flex h-6 w-6 items-center justify-center rounded-full text-slate-400 transition hover:bg-violet-50 hover:text-violet-600"
                      >
                        <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                          <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0z" />
                        </svg>
                      </button>
                      <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ${st.bg} ${st.text}`}>
                        {st.label}
                      </span>
                    </div>
                  </div>
                  {course.description && (
                    <p
                      onClick={() => navigate(`/admin/study-materials/edit/${course.id}`)}
                      className="mb-3 line-clamp-2 cursor-pointer text-xs text-slate-500"
                    >
                      {course.description}
                    </p>
                  )}
                  <div
                    onClick={() => navigate(`/admin/study-materials/edit/${course.id}`)}
                    className="mt-auto flex cursor-pointer items-center gap-3 pt-2 text-xs text-slate-400 border-t border-slate-100"
                  >
                    <span className="flex items-center gap-1">
                      <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 10h16M4 14h8" />
                      </svg>
                      {lessonCount} lesson{lessonCount !== 1 ? 's' : ''}
                    </span>
                    <span className="flex items-center gap-1" title="Staff this course is shared with">
                      <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a4 4 0 0 0-3-3.87M9 20H4v-2a4 4 0 0 1 3-3.87m9-5.13a4 4 0 1 1-8 0 4 4 0 0 1 8 0zm6 8v-1a4 4 0 0 0-3-3.87m-3-4.13a4 4 0 1 1 0 7.75" />
                      </svg>
                      {course.staffCount || 0} staff
                    </span>
                    <span className="ml-auto flex items-center gap-1">
                      <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0V11.25A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5" />
                      </svg>
                      {fmtDate(course.createdAt)}
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-0 border-t border-slate-100 bg-slate-50">
                  <button
                    onClick={() => togglePublish(course)}
                    className={`flex flex-1 items-center justify-center gap-1.5 py-2.5 text-xs font-semibold transition ${
                      course.status === 'published' ? 'text-amber-600 hover:bg-amber-50' : 'text-emerald-600 hover:bg-emerald-50'
                    }`}
                  >
                    {course.status === 'published' ? (
                      <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 9.75a3 3 0 1 0 4.5 4.5m-4.5-4.5L14.25 14.25M9.75 9.75 4.5 4.5m5.25 5.25L2.25 12s3.75 6.75 9.75 6.75c1.352 0 2.605-.345 3.717-.933m2.25-1.5A11.62 11.62 0 0 0 21.75 12S18 5.25 12 5.25c-.844 0-1.65.13-2.4.362M14.25 14.25 19.5 19.5" />
                      </svg>
                    ) : (
                      <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0z" />
                      </svg>
                    )}
                    {course.status === 'published' ? 'Unpublish' : 'Publish'}
                  </button>
                  <div className="w-px h-8 bg-slate-200" />
                  <button
                    onClick={() => setEditingCourse(course)}
                    className="flex flex-1 items-center justify-center gap-1.5 py-2.5 text-xs font-semibold text-slate-600 hover:bg-slate-100 transition"
                  >
                    <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L6.832 18.82 2.25 20.25l1.43-4.582 13.182-13.181z" />
                    </svg>
                    Edit
                  </button>
                  <div className="w-px h-8 bg-slate-200" />
                  <button
                    onClick={() => handleDelete(course)}
                    className="flex flex-1 items-center justify-center gap-1.5 py-2.5 text-xs font-semibold text-red-500 hover:bg-red-50 transition"
                  >
                    <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
                    </svg>
                    Delete
                  </button>
                </div>

                <div className="space-y-1.5 border-t border-slate-100 p-2">
                  <button
                    onClick={() => navigate(`/admin/study-materials/edit/${course.id}`)}
                    className="flex w-full items-center justify-center gap-1.5 rounded-lg bg-violet-600 py-2 text-xs font-semibold text-white shadow-sm hover:bg-violet-700 transition"
                  >
                    <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                    </svg>
                    Add Content
                  </button>
                  <button
                    onClick={() => setSharingCourse(course)}
                    className="flex w-full items-center justify-center gap-1.5 rounded-lg border border-slate-200 py-2 text-xs font-semibold text-slate-600 hover:border-violet-300 hover:text-violet-700 transition"
                  >
                    <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M7.217 10.907a2.25 2.25 0 1 0 0 2.186m0-2.186c.18.324.283.696.283 1.093s-.103.77-.283 1.093m0-2.186 9.566-5.314m-9.566 7.5 9.566 5.314m0 0a2.25 2.25 0 1 0 3.935 2.186 2.25 2.25 0 0 0-3.935-2.186zm0-12.814a2.25 2.25 0 1 0 3.933-2.185 2.25 2.25 0 0 0-3.933 2.185z" />
                    </svg>
                    Share with Batches{course.batchCount ? ` (${course.batchCount})` : ''}
                  </button>
                  <button
                    onClick={() => setSharingStaffCourse(course)}
                    className="flex w-full items-center justify-center gap-1.5 rounded-lg border border-slate-200 py-2 text-xs font-semibold text-slate-600 hover:border-violet-300 hover:text-violet-700 transition"
                  >
                    <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a4 4 0 0 0-3-3.87M9 20H4v-2a4 4 0 0 1 3-3.87m9-5.13a4 4 0 1 1-8 0 4 4 0 0 1 8 0zm6 8v-1a4 4 0 0 0-3-3.87m-3-4.13a4 4 0 1 1 0 7.75" />
                    </svg>
                    Share with Staffs{course.staffCount ? ` (${course.staffCount})` : ''}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
        )}
      </div>

      {showNewModal && (
        <NewCourseModal
          onClose={() => setShowNewModal(false)}
          onCreated={(course) => {
            setCourses((prev) => [...prev, course]);
            setShowNewModal(false);
            navigate(`/admin/study-materials/edit/${course.id}`);
          }}
        />
      )}

      {editingCourse && (
        <EditCourseModal
          course={editingCourse}
          onClose={() => setEditingCourse(null)}
          onSaved={(updated) => {
            setCourses((prev) => prev.map((c) => (c.id === updated.id ? { ...c, ...updated, lessonCount: countLessons(updated.tree) } : c)));
            setEditingCourse(null);
          }}
        />
      )}

      {sharingCourse && (
        <ShareBatchesModal
          course={sharingCourse}
          onClose={() => setSharingCourse(null)}
          onChanged={() => {
            getCourseBatches(sharingCourse.id).then((rows) => {
              setCourses((prev) => prev.map((c) => (c.id === sharingCourse.id ? { ...c, batchCount: rows.length } : c)));
            });
          }}
        />
      )}

      {sharingStaffCourse && (
        <ShareStaffModal
          course={sharingStaffCourse}
          onClose={() => setSharingStaffCourse(null)}
          onChanged={() => {
            getCourseStaff(sharingStaffCourse.id).then((rows) => {
              setCourses((prev) => prev.map((c) => (c.id === sharingStaffCourse.id ? { ...c, staffCount: rows.length } : c)));
            });
          }}
        />
      )}
    </div>
  );
}
